import React, { useState } from 'react';
import { Search, Plus, Minus, X, ShoppingCart } from 'lucide-react';
import type { Sale, SaleItem } from '../../types/sale';
import type { Customer } from '../../types/customer';
import type { Product } from '../../types/product';

interface SaleFormProps {
  onSubmit: (data: Sale) => void;
  onCancel: () => void;
}

const SaleForm: React.FC<SaleFormProps> = ({ onSubmit, onCancel }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [items, setItems] = useState<SaleItem[]>([]);
  const [searchProduct, setSearchProduct] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<Sale['paymentMethod']>('cash');
  const [notes, setNotes] = useState('');
  const [discount, setDiscount] = useState(0);

  // Mock data - replace with actual data from your state management
  const products: Product[] = [
    {
      id: '1',
      name: 'Produto Exemplo',
      internalCode: 'PRD001',
      barcode: '7894561230',
      category: 'Eletrônicos',
      subcategory: 'Smartphones',
      unit: 'UN',
      costPrice: 1500,
      sellingPrice: 2500,
      minStock: 5,
      currentStock: 10,
      status: 'active',
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  const handleAddItem = (product: Product) => {
    const existingItem = items.find(item => item.productId === product.id);
    
    if (existingItem) {
      setItems(items.map(item =>
        item.productId === product.id
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unitPrice }
          : item
      ));
    } else {
      const newItem: SaleItem = {
        id: crypto.randomUUID(),
        productId: product.id,
        product,
        quantity: 1,
        unitPrice: product.sellingPrice,
        discount: 0,
        total: product.sellingPrice
      };
      setItems([...items, newItem]);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    if (quantity < 1) return;
    
    setItems(items.map(item =>
      item.id === itemId
        ? { ...item, quantity, total: quantity * item.unitPrice - item.discount }
        : item
    ));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
    const total = subtotal - discount;
    return { subtotal, total };
  };

  const handleSubmit = () => {
    const { subtotal, total } = calculateTotals();
    const sale: Sale = {
      id: crypto.randomUUID(),
      customer,
      items,
      subtotal,
      discount,
      total,
      paymentMethod,
      status: 'pending',
      notes,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    onSubmit(sale);
  };

  const { subtotal, total } = calculateTotals();

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Nova Venda</h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Customer Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cliente
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Buscar cliente..."
              className="w-full pl-10 pr-4 py-2 border rounded-md"
            />
          </div>
        </div>

        {/* Product Search and List */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Produtos
          </label>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              placeholder="Buscar produto por nome ou código..."
              className="w-full pl-10 pr-4 py-2 border rounded-md"
            />
          </div>

          {/* Product Results */}
          {searchProduct && (
            <div className="mb-4 border rounded-md divide-y">
              {products.map(product => (
                <div
                  key={product.id}
                  className="p-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                  onClick={() => handleAddItem(product)}
                >
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-sm text-gray-500">Código: {product.internalCode}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">R$ {product.sellingPrice.toFixed(2)}</p>
                    <p className="text-sm text-gray-500">Estoque: {product.currentStock}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Selected Items */}
          <div className="border rounded-md">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Produto</th>
                  <th className="px-4 py-2 text-center text-sm font-medium text-gray-500">Qtd</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">Preço Un.</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">Total</th>
                  <th className="px-4 py-2 text-center text-sm font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map(item => (
                  <tr key={item.id}>
                    <td className="px-4 py-2">
                      <p className="font-medium">{item.product.name}</p>
                      <p className="text-sm text-gray-500">Código: {item.product.internalCode}</p>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-12 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      R$ {item.unitPrice.toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      R$ {item.total.toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment Details */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Forma de Pagamento
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as Sale['paymentMethod'])}
              className="w-full border rounded-md p-2"
            >
              <option value="cash">Dinheiro</option>
              <option value="credit_card">Cartão de Crédito</option>
              <option value="debit_card">Cartão de Débito</option>
              <option value="pix">PIX</option>
              <option value="bank_slip">Boleto</option>
              <option value="credit">Crediário</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Desconto
            </label>
            <input
              type="number"
              value={discount}
              onChange={(e) => setDiscount(Number(e.target.value))}
              min="0"
              step="0.01"
              className="w-full border rounded-md p-2"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Observações
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full border rounded-md p-2"
          />
        </div>

        {/* Totals */}
        <div className="border-t pt-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-medium">R$ {subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">Desconto:</span>
            <span className="font-medium">R$ {discount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center text-lg font-bold">
            <span>Total:</span>
            <span>R$ {total.toFixed(2)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 border rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={items.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 flex items-center"
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            Finalizar Venda
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaleForm;